import {
  type BookedFlightItineraryWithDepartureTime,
  type FlightAction,
  FlightActionDestination,
  FlightActionState,
} from "@b2bportal/air-booking-api";

/**
 * How a post-booking flight action should be routed, resolved from the
 * BFF-computed capability result
 * (`BookedFlightItineraryWithDepartureTime.actionCapabilities`).
 *
 * This union is the whole routing matrix: the BFF decides which of
 * cancel / CFAR / DAFAR are actionable, in which portal, and under which
 * identifier, and when it answers it is the ONLY routing source. Callers
 * must never fall back to contract presence, user cohort, or booking
 * origin to re-derive an action.
 */
export type FlightActionRoute =
  /**
   * The BFF sent no capabilities for this itinerary, i.e. it does not
   * compute them for this tenant or the response predates the field.
   * This is the fail-open: callers keep their pre-existing behavior.
   * Deliberately distinct from an empty list, which means "computed,
   * nothing actionable".
   */
  | { kind: "not-computed" }
  /** Answered: run the pre-existing in-app b2bportal flow. */
  | { kind: "legacy" }
  /** Answered: hand the action off to the commerce portal under `identifier`. */
  | { kind: "commerce"; identifier: string }
  /**
   * Answered as not actionable (or malformed): callers must not enter the
   * action's flow. The fallback surface is caller-specific, e.g. hiding
   * the CTA in a details screen or landing a deeplink on trip details.
   */
  | {
      kind: "unavailable";
      state?: FlightActionState;
      failureReason?: string;
    };

/**
 * Resolves the route for a single action. Total: every input maps to one
 * of the four kinds, so callers can switch exhaustively.
 *
 * `destination` and `identifier` are populated if and only if the state is
 * `Available`, but both are optional on the wire type. A commerce answer
 * missing its identifier is therefore treated as unavailable rather than
 * routed to `undefined`. A legacy answer needs no identifier: the in-app
 * flow reads the itinerary id off the flight itself.
 */
export function resolveFlightActionRoute(
  flight: BookedFlightItineraryWithDepartureTime | null | undefined,
  action: FlightAction
): FlightActionRoute {
  const capabilities = flight?.actionCapabilities;
  if (!capabilities) {
    return { kind: "not-computed" };
  }

  // The BFF guarantees at most one entry per action, so the first match is
  // the only match. A present list with no entry for the action is still an
  // answer, and the answer is "no".
  const capability = capabilities.find((entry) => entry.action === action);
  if (!capability) {
    return { kind: "unavailable" };
  }

  if (capability.state !== FlightActionState.Available) {
    return {
      kind: "unavailable",
      state: capability.state,
      failureReason: capability.failureReason,
    };
  }

  if (
    capability.destination === FlightActionDestination.Commerce &&
    capability.identifier
  ) {
    return { kind: "commerce", identifier: capability.identifier };
  }

  if (capability.destination === FlightActionDestination.Legacy) {
    return { kind: "legacy" };
  }

  return { kind: "unavailable", state: capability.state };
}

/**
 * Whether the pre-existing in-app b2bportal flow for the action may run:
 * either the BFF routed it to `Legacy`, or it did not answer at all. The
 * second case is what keeps non-participating tenants (uber) byte-for-byte
 * unchanged.
 */
export function allowsInAppFlightAction(route: FlightActionRoute): boolean {
  return route.kind === "legacy" || route.kind === "not-computed";
}
